/**
 * Thin wrapper around the Telegram WebApp JS SDK (loaded via the
 * `telegram-web-app.js` script tag in index.html). We never invent or
 * accept a client-supplied userId; we only ever forward the raw `initData`
 * string to the backend, which verifies it cryptographically.
 */

declare global {
	interface Window {
		Telegram?: {
			WebApp: {
				initData: string
				initDataUnsafe: { user?: { id: number; first_name: string; username?: string } ; start_param?: string }
				ready: () => void
				expand: () => void
				close: () => void
				openTelegramLink: (url: string) => void
				showAlert?: (message: string) => void
				BackButton: { show: () => void; hide: () => void; onClick: (fn: () => void) => void }
				HapticFeedback?: { impactOccurred: (style: string) => void; notificationOccurred: (type: string) => void }
				themeParams: Record<string, string>
				colorScheme: "light" | "dark"
			}
		}
	}
}

export function getTelegramWebApp() {
	return typeof window !== "undefined" ? window.Telegram?.WebApp : undefined
}

export function getRawInitData(): string {
	const webApp = getTelegramWebApp()
	if (!webApp || !webApp.initData) {
		// Not running inside Telegram (e.g. local dev in a normal browser).
		// The backend will reject an empty/invalid initData with AUTH_INVALID.
		return ""
	}
	return webApp.initData
}

export function getStartParam(): string | undefined {
	return getTelegramWebApp()?.initDataUnsafe?.start_param
}

export function initTelegramWebApp(): void {
	const webApp = getTelegramWebApp()
	webApp?.ready()
	webApp?.expand()
}

export function haptic(style: "light" | "medium" | "heavy" = "light"): void {
	getTelegramWebApp()?.HapticFeedback?.impactOccurred(style)
}

/**
 * Builds a deep link that opens this bot's Mini App directly with a given
 * start param (e.g. a room invite code). Requires VITE_TELEGRAM_BOT_USERNAME
 * to be set at build time; without it we cannot construct a working
 * Telegram deep link, so callers must handle a null return.
 */
export function buildBotStartLink(startParam: string): string | null {
	const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME
	if (!botUsername) return null
	return "https://t.me/" + botUsername + "?startapp=" + startParam
}

/**
 * Shares a link using Telegram's native share sheet when running inside the
 * Telegram WebApp, falling back to copying the link to the clipboard.
 * `navigator.share` is not implemented by the Telegram in-app WebView (on
 * mobile or desktop), so relying on it alone silently does nothing when
 * tapped -- this is why the previous "invite friends" button appeared to do
 * nothing.
 */
export async function shareLink(url: string, text?: string): Promise<"shared" | "copied" | "failed"> {
	const webApp = getTelegramWebApp()
	if (webApp) {
		const shareUrl = "https://t.me/share/url?url=" + encodeURIComponent(url) + (text ? "&text=" + encodeURIComponent(text) : "")
		webApp.openTelegramLink(shareUrl)
		return "shared"
	}
	if (typeof navigator !== "undefined" && navigator.share) {
		try {
			await navigator.share({ url, text })
			return "shared"
		} catch {
			// user cancelled or share failed; fall through to clipboard
		}
	}
	if (typeof navigator !== "undefined" && navigator.clipboard) {
		try {
			await navigator.clipboard.writeText(url)
			return "copied"
		} catch {
			// ignore
		}
	}
	return "failed"
}